import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Paper, Stack, Group, Text, Badge, Button, Divider,
  ThemeIcon, Progress, Code, Box, Textarea,
} from '@mantine/core';
import {
  IconCrown, IconAnalyze, IconSearch, IconBulb,
  IconChartBar, IconShieldCheck, IconTarget,
  IconTrendingDown, IconSettings, IconRobot, IconPlus, IconPencil,
} from '@tabler/icons-react';
import { saveAgent } from '../../redux/slices/agentsSlice';

const AGENT_ICONS = {
  orchestrator:               IconCrown,
  context_analyst:            IconAnalyze,
  risk_discovery:             IconSearch,
  scenario_builder:           IconBulb,
  risk_evaluator:             IconChartBar,
  security_analyst:           IconShieldCheck,
  mitigating_actions_planner: IconTarget,
  residual_analyst:           IconTrendingDown,
};

const TOOL_LABELS = {
  search_model_card:          'Search Model Card',
  search_risk_catalogues:     'Search Risk Catalogues',
  search_governance_policies: 'Search Governance Policies',
  search_security_policies:   'Search Security Policies',
  search_ai_policies:         'Search AI Policies',
  get_assessment_state:       'Get Assessment State',
  evaluate_output:            'Evaluate Output Quality',
  delegate:                   'Delegate (Orchestrator)',
  check_progress:             'Check Progress',
  evaluate_completeness:      'Evaluate Completeness',
};

const PIPELINE_ORDER = [
  'context_analyst',
  'risk_discovery',
  'scenario_builder',
  'risk_evaluator',
  'security_analyst',
  'mitigating_actions_planner',
  'residual_analyst',
];

export default function AgentDetailPanel({ agent, onEdit }) {
  const dispatch = useDispatch();
  const [editingInstructions, setEditingInstructions] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useState('');

  if (!agent) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <Text c="dimmed" ta="center">Select an agent to view details</Text>
      </Paper>
    );
  }

  const Icon = AGENT_ICONS[agent.agent_key] || IconRobot;
  const isOrchestrator = agent.agent_category === 'orchestrator';
  const accentColor = isOrchestrator ? 'violet' : 'blue';
  const qualityPct = (agent.quality_threshold || 0.7) * 100;
  const stepIndex = PIPELINE_ORDER.indexOf(agent.agent_key);
  const hasCriteria = agent.decision_criteria && Object.keys(agent.decision_criteria).length > 0;

  return (
    <Paper p="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <ThemeIcon size={40} radius="md" variant="light" color={accentColor}>
              <Icon size={22} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={600} size="lg">{agent.agent_name}</Text>
              <Group gap={6}>
                {stepIndex >= 0 && (
                  <Badge size="xs" variant="filled" color="gray">Step {stepIndex + 1}</Badge>
                )}
                <Badge size="xs" variant="light" color={accentColor}>
                  {agent.agent_category}
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  v{agent.version || 1}
                </Badge>
                {agent.isCustomized && (
                  <Badge size="xs" variant="light" color="orange">Customized</Badge>
                )}
              </Group>
            </Stack>
          </Group>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconSettings size={14} />}
            onClick={() => onEdit(agent)}
          >
            Edit
          </Button>
        </Group>

        {/* Description */}
        {agent.agent_description && (
          <>
            <Divider label="Description" labelPosition="left" />
            <Text size="sm" c="dimmed">{agent.agent_description}</Text>
          </>
        )}

        {/* Role */}
        {agent.role && (
          <>
            <Divider label="Role" labelPosition="left" />
            <Text size="sm">{agent.role}</Text>
          </>
        )}

        {/* Goal */}
        {agent.goal && (
          <>
            <Divider label="Goal" labelPosition="left" />
            <Text size="sm">{agent.goal}</Text>
          </>
        )}

        {/* Prompts */}
        <Divider label="Prompts" labelPosition="left" />
        <Group gap="xs" wrap="wrap">
          {(agent.prompt_keys || []).length > 0 ? (
            agent.prompt_keys.map(key => (
              <Badge key={key} variant="outline" size="sm">{key}</Badge>
            ))
          ) : (
            <Text size="xs" c="dimmed">No prompts assigned</Text>
          )}
        </Group>

        {/* Tools */}
        <Divider label="Tools" labelPosition="left" />
        <Group gap="xs" wrap="wrap">
          {(agent.tools || []).length > 0 ? (
            agent.tools.map(tool => (
              <Badge key={tool} variant="dot" size="sm" color="teal">
                {TOOL_LABELS[tool] || tool}
              </Badge>
            ))
          ) : (
            <Text size="xs" c="dimmed">No tools assigned</Text>
          )}
        </Group>

        {/* Skills */}
        {(agent.skills || []).length > 0 && (
          <>
            <Divider label="Skills" labelPosition="left" />
            <Group gap="xs" wrap="wrap">
              {agent.skills.map(skill => (
                <Badge key={skill} variant="dot" size="sm" color="grape">{skill}</Badge>
              ))}
            </Group>
          </>
        )}

        {/* Quality Settings */}
        <Divider label="Quality Settings" labelPosition="left" />
        <Box>
          <Group justify="space-between" mb={4}>
            <Text size="sm">Quality Threshold</Text>
            <Text size="sm" fw={600}>{qualityPct.toFixed(0)}%</Text>
          </Group>
          <Progress value={qualityPct} size="sm" color={accentColor} radius="xl" />
          <Text size="xs" c="dimmed" mt={6}>
            Max iterations: {agent.max_iterations || 3}
          </Text>
        </Box>

        {/* Instructions */}
        <Divider
          label={
            <Group gap={6}>
              <Text size="sm">Instructions</Text>
              {!editingInstructions && agent.agent_instructions && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPencil size={12} />}
                  onClick={() => {
                    setInstructionsDraft(agent.agent_instructions);
                    setEditingInstructions(true);
                  }}
                >
                  Edit
                </Button>
              )}
            </Group>
          }
          labelPosition="left"
        />
        {editingInstructions ? (
          <Stack gap="xs">
            <Textarea
              value={instructionsDraft}
              onChange={(e) => setInstructionsDraft(e.currentTarget.value)}
              placeholder={'## Focus Areas\n- Prioritize regulatory compliance risks\n- Always consider GDPR and EU AI Act\n\n## Constraints\n- Maximum 5 risks per assessment area'}
              autosize
              minRows={6}
              maxRows={16}
              styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setEditingInstructions(false)}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                onClick={() => {
                  dispatch(saveAgent({
                    agent_key: agent.agent_key,
                    agent_instructions: instructionsDraft,
                  }));
                  setEditingInstructions(false);
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        ) : agent.agent_instructions ? (
          <Paper p="sm" radius="sm" bg="gray.0">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {agent.agent_instructions}
            </Text>
          </Paper>
        ) : (
          <Group gap="xs">
            <Text size="xs" c="dimmed" fs="italic">No custom instructions</Text>
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconPlus size={12} />}
              onClick={() => {
                setInstructionsDraft('');
                setEditingInstructions(true);
              }}
            >
              Add Instructions
            </Button>
          </Group>
        )}

        {/* Decision Criteria */}
        {hasCriteria && (
          <>
            <Divider label="Decision Criteria" labelPosition="left" />
            <Code block>{JSON.stringify(agent.decision_criteria, null, 2)}</Code>
          </>
        )}
      </Stack>
    </Paper>
  );
}
